import React, { useState, useEffect } from 'react';
import { MessageCircle, RefreshCw, Trash2, User, Bot, Clock, Globe, Search, ChevronDown, ChevronUp, Send, Reply } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { admin } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGE_FLAGS = {
  tr: '🇹🇷',
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  ar: '🇸🇦',
  ja: '🇯🇵',
  pt: '🇧🇷',
  ru: '🇷🇺',
  zh: '🇨🇳',
};

const ChatManagementTab = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSessions, setExpandedSessions] = useState({});
  const [sessionDetails, setSessionDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState({});
  const [replyMessage, setReplyMessage] = useState({});
  const [sendingReply, setSendingReply] = useState({});
  const [waOpen, setWaOpen] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waForm, setWaForm] = useState({
    enabled: false,
    provider: 'callmebot',
    admin_phone: '',
    phone_number_id: '',
    access_token: '',
    verify_token: '',
    template_name: '',
    template_lang: 'tr',
    callmebot_apikey: '',
    telegram_bot_token: '',
    telegram_chat_id: '',
    webhook_url: '',
    has_access_token: false,
    has_verify_token: false,
    has_callmebot_apikey: false,
    has_telegram_bot_token: false,
  });

  useEffect(() => {
    fetchSessions();
    loadWhatsappSettings();
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadWhatsappSettings = async () => {
    try {
      const { data } = await admin.getWhatsappChatSettings();
      setWaForm((prev) => ({
        ...prev,
        enabled: !!data.enabled,
        provider: data.provider || 'callmebot',
        admin_phone: data.admin_phone || '',
        phone_number_id: data.phone_number_id || '',
        template_name: data.template_name || '',
        template_lang: data.template_lang || 'tr',
        telegram_chat_id: data.telegram_chat_id || '',
        webhook_url: data.webhook_url || '',
        has_access_token: !!data.has_access_token,
        has_verify_token: !!data.has_verify_token,
        has_callmebot_apikey: !!data.has_callmebot_apikey || !!(data.callmebot_apikey || '').trim(),
        has_telegram_bot_token: !!data.has_telegram_bot_token,
        access_token: '',
        verify_token: '',
        callmebot_apikey: data.callmebot_apikey || '',
        telegram_bot_token: '',
      }));
    } catch {
      /* ayarlar yoksa sessiz */
    }
  };

  const saveWhatsappSettings = async () => {
    setWaSaving(true);
    try {
      const payload = {
        enabled: waForm.enabled,
        provider: waForm.provider,
        admin_phone: waForm.admin_phone,
        phone_number_id: waForm.phone_number_id,
        template_name: waForm.template_name,
        template_lang: waForm.template_lang || 'tr',
        telegram_chat_id: waForm.telegram_chat_id,
      };
      if (waForm.access_token.trim()) payload.access_token = waForm.access_token.trim();
      if (waForm.verify_token.trim()) payload.verify_token = waForm.verify_token.trim();
      if (waForm.callmebot_apikey.trim()) payload.callmebot_apikey = waForm.callmebot_apikey.trim();
      if (waForm.telegram_bot_token.trim()) payload.telegram_bot_token = waForm.telegram_bot_token.trim();
      await admin.updateWhatsappChatSettings(payload);
      toast.success('WhatsApp sohbet ayarları kaydedildi');
      await loadWhatsappSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setWaSaving(false);
    }
  };

  const testWhatsapp = async () => {
    setWaTesting(true);
    try {
      const { data } = await admin.testWhatsappChat({
        message: 'Turkish E-Market sohbet testi — bu mesaji goruyorsaniz baglanti calisiyor.',
        admin_phone: waForm.admin_phone,
        callmebot_apikey: waForm.callmebot_apikey.trim() || undefined,
      });
      toast.success(
        data?.hint ||
          data?.detail ||
          'Sıraya alındı. WhatsApp’ta CallMeBot (+34 694 29 84 96) sohbetine bakın.',
      );
      await loadWhatsappSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test gönderilemedi');
    } finally {
      setWaTesting(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/chat-sessions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        let msg = 'Konuşmalar yüklenemedi';
        try {
          const err = await response.json();
          msg = err.detail || msg;
        } catch {
          /* ignore */
        }
        toast.error(msg);
        setSessions([]);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Konuşmalar yüklenemedi');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/chat-sessions/${encodeURIComponent(sessionId)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.success(
          data?.deleted_messages > 0 ? 'Oturum silindi' : 'Oturum kaydı yoktu veya zaten silinmişti',
        );
        fetchSessions();
        if (selectedSession?.id === sessionId) {
          setSelectedSession(null);
        }
      } else {
        let msg = 'Oturum silinemedi';
        try {
          const err = await response.json();
          msg = err.detail || msg;
        } catch {
          /* ignore */
        }
        toast.error(msg);
      }
    } catch (error) {
      console.error(error);
      toast.error('Oturum silinemedi');
    }
  };

  const loadSessionDetail = async (sessionId) => {
    setLoadingDetail((prev) => ({ ...prev, [sessionId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/chat-sessions/${encodeURIComponent(sessionId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.ok) {
        const data = await response.json();
        setSessionDetails((prev) => ({ ...prev, [sessionId]: data }));
      }
    } catch (error) {
      console.error('Failed to load session detail:', error);
    } finally {
      setLoadingDetail((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  const toggleSession = (sessionId) => {
    const willExpand = !expandedSessions[sessionId];
    setExpandedSessions((prev) => ({
      ...prev,
      [sessionId]: willExpand,
    }));
    if (willExpand) {
      loadSessionDetail(sessionId);
    }
  };

  const handleReplyChange = (sessionId, value) => {
    setReplyMessage(prev => ({
      ...prev,
      [sessionId]: value
    }));
  };

  const sendAdminReply = async (sessionId) => {
    const message = replyMessage[sessionId]?.trim();
    if (!message) return;

    setSendingReply(prev => ({ ...prev, [sessionId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/chat-sessions/${encodeURIComponent(sessionId)}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message }),
        },
      );

      if (response.ok) {
        toast.success('Cevap gönderildi');
        setReplyMessage((prev) => ({ ...prev, [sessionId]: '' }));
        await loadSessionDetail(sessionId);
        fetchSessions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Cevap gönderilemedi');
      }
    } catch (error) {
      toast.error('Cevap gönderilemedi');
    } finally {
      setSendingReply(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      session.id?.toLowerCase().includes(query) ||
      session.messages?.some(msg => msg.content?.toLowerCase().includes(query))
    );
  });

  const formatMessageClock = (msg) => {
    const raw = msg?.timestamp || msg?.created_at;
    if (!raw) return '—';
    const d = Date.parse(raw);
    if (!Number.isFinite(d)) return '—';
    return new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const getLastMessage = (session) => {
    const messages = session.messages || [];
    return messages[messages.length - 1];
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Şimdi';
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setWaOpen((v) => !v)}>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Şirket WhatsApp bildirimi</span>
            <Badge variant={waForm.enabled ? 'default' : 'outline'}>
              {waForm.enabled ? 'Açık' : 'Kapalı'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Müşteri siteden yazınca sohbet kayda düşer; şirket WhatsApp hattına “Yeni mesajlarınız var” gider.
          </CardDescription>
        </CardHeader>
        {waOpen && (
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label>Bildirimler</Label>
                <p className="text-xs text-muted-foreground">Müşteri yazınca size mesaj gider</p>
                {!waForm.enabled && (
                  <p className="mt-1 text-xs text-amber-700">Kapalı — siteden gelen sohbet WhatsApp’a gitmez.</p>
                )}
              </div>
              <Switch
                checked={waForm.enabled}
                onCheckedChange={(v) => setWaForm((p) => ({ ...p, enabled: v }))}
              />
            </div>
            <div>
              <Label>Nasıl gönderilsin?</Label>
              <select
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={waForm.provider}
                onChange={(e) => setWaForm((p) => ({ ...p, provider: e.target.value }))}
              >
                <option value="callmebot">WhatsApp bildirimi (CallMeBot — Meta yok, 5 dk)</option>
                <option value="telegram">Telegram (bildirim + sizin cevabınız siteye düşer)</option>
                <option value="meta">Meta Cloud API (zor, resmi iki yönlü WhatsApp)</option>
              </select>
            </div>

            {waForm.provider === 'callmebot' && (
              <div className="space-y-3 rounded-lg border p-3">
                <p className="text-sm">
                  1) WhatsApp’ta{' '}
                  <a
                    className="text-blue-600 underline"
                    href="https://www.callmebot.com/blog/free-api-whatsapp-messages/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    CallMeBot talimatı
                  </a>
                  ’ndaki numarayı ekleyin.
                  <br />
                  2) <code>I allow callmebot to send me messages</code> yazın.
                  <br />
                  3) Gelen API key’i aşağı yapıştırın.
                </p>
                <div>
                  <Label>Şirket WhatsApp hattı</Label>
                  <Input
                    placeholder="Footer’daki şirket numarası, örn. +90 5xx…"
                    value={waForm.admin_phone}
                    onChange={(e) => setWaForm((p) => ({ ...p, admin_phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>CallMeBot API key {waForm.callmebot_apikey ? '(kayıtlı)' : ''}</Label>
                  <Input
                    type="text"
                    autoComplete="off"
                    placeholder="Örn. 8620640"
                    value={waForm.callmebot_apikey}
                    onChange={(e) => setWaForm((p) => ({ ...p, callmebot_apikey: e.target.value }))}
                  />
                  {waForm.callmebot_apikey ? (
                    <p className="mt-1 text-xs text-green-700">Kayıtlı anahtar: {waForm.callmebot_apikey}</p>
                  ) : (
                    <p className="mt-1 text-xs text-amber-700">Anahtar boş — yapıştırıp Kaydet veya Test’e basın.</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  CallMeBot yalnızca size bildirim atar. WhatsApp’tan yazdığınız müşteriye gitmez.
                  Cevabı <strong>Admin → Sohbet</strong> kutusundan yazın; müşteri sitede görür.
                </p>
              </div>
            )}

            {(waForm.provider === 'telegram' || waForm.provider === 'callmebot') && (
              <div className="space-y-3 rounded-lg border p-3">
                <p className="text-sm font-medium">Telefondan cevap (Telegram)</p>
                <p className="text-sm">
                  WhatsApp’tan cevap bu sistemle mümkün değil. Telefondan yazmak isterseniz Telegram botu
                  ekleyin: sizin mesajınız site sohbetine düşer. WhatsApp bildirimi açık kalır.
                  <br />
                  1) Telegram’da <strong>@BotFather</strong> → /newbot
                  <br />
                  2) Token’ı yapıştırıp kaydedin
                  <br />
                  3) Kendi botunuza /start yazın — chat_id otomatik gelir
                </p>
                <div>
                  <Label>Bot token {waForm.has_telegram_bot_token ? '(kayıtlı)' : ''}</Label>
                  <Input
                    type="password"
                    placeholder={waForm.has_telegram_bot_token ? 'Değiştirmek için yeni token' : '123456:ABC...'}
                    value={waForm.telegram_bot_token}
                    onChange={(e) => setWaForm((p) => ({ ...p, telegram_bot_token: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Chat ID ( /start sonrası dolar )</Label>
                  <Input
                    placeholder="Boş bırakılabilir"
                    value={waForm.telegram_chat_id}
                    onChange={(e) => setWaForm((p) => ({ ...p, telegram_chat_id: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {waForm.provider === 'meta' && (
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Sizin WhatsApp numaranız</Label>
                  <Input
                    placeholder="+90 5xx xxx xx xx"
                    value={waForm.admin_phone}
                    onChange={(e) => setWaForm((p) => ({ ...p, admin_phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Phone Number ID</Label>
                  <Input
                    placeholder="Meta → WhatsApp → API Setup"
                    value={waForm.phone_number_id}
                    onChange={(e) => setWaForm((p) => ({ ...p, phone_number_id: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Access Token {waForm.has_access_token ? '(kayıtlı)' : ''}</Label>
                  <Input
                    type="password"
                    placeholder={waForm.has_access_token ? 'Değiştirmek için yeni token' : 'Permanent token'}
                    value={waForm.access_token}
                    onChange={(e) => setWaForm((p) => ({ ...p, access_token: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Webhook Verify Token {waForm.has_verify_token ? '(kayıtlı)' : ''}</Label>
                  <Input
                    placeholder="Kendiniz belirleyin, Meta’ya aynı değeri yazın"
                    value={waForm.verify_token}
                    onChange={(e) => setWaForm((p) => ({ ...p, verify_token: e.target.value }))}
                  />
                </div>
                {waForm.webhook_url && (
                  <p className="text-xs text-muted-foreground break-all md:col-span-2">
                    Meta Callback URL: <code className="bg-muted px-1 rounded">{waForm.webhook_url}</code>
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={saveWhatsappSettings} disabled={waSaving}>
                {waSaving ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
              <Button variant="outline" onClick={testWhatsapp} disabled={waTesting}>
                {waTesting ? 'Gönderiliyor…' : 'Test mesajı gönder'}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sessions.length}</p>
                <p className="text-sm text-muted-foreground">Toplam Oturum</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {sessions.reduce((acc, s) => acc + (s.messages?.filter(m => m.role === 'user').length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Müşteri Mesajı</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bot className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {sessions.reduce((acc, s) => acc + (s.messages?.filter(m => m.role === 'assistant').length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">AI Yanıtı</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Globe className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(sessions.map(s => s.language)).size}
                </p>
                <p className="text-sm text-muted-foreground">Farklı Dil</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Refresh */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Mesajlarda ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={fetchSessions} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Chat Sessions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Müşteri Destek Konuşmaları
          </CardTitle>
          <CardDescription>
            AI destekli müşteri destek chatinden gelen tüm konuşmalar
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Henüz konuşma yok</p>
              <p className="text-sm">Müşteriler chat widget kullandığında burada görünecek</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSessions.map((session) => {
                const detail = sessionDetails[session.id];
                const messages = detail?.messages?.length ? detail.messages : (session.messages || []);
                const lastMsg = messages[messages.length - 1] || getLastMessage(session);
                const isExpanded = expandedSessions[session.id];
                const preview = lastMsg?.content || '';
                
                return (
                  <div 
                    key={session.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Session Header */}
                    <div 
                      className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSession(session.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                          {session.language ? LANGUAGE_FLAGS[session.language] : '👤'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Oturum #{session.id?.slice(-6)}</span>
                            <Badge variant="outline" className="text-xs">
                              {detail?.message_count || messages.length || 0} mesaj
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 break-words">
                            {preview}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatTime(session.updated_at || session.created_at)}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Messages */}
                    {isExpanded && (
                      <div className="border-t">
                        <div className="max-h-[70vh] min-h-[180px] overflow-y-auto overflow-x-hidden p-4 space-y-3 bg-white">
                            {loadingDetail[session.id] && !messages.length ? (
                              <div className="flex items-center justify-center py-8 text-muted-foreground">
                                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                                Konuşma yükleniyor...
                              </div>
                            ) : (
                              messages.map((msg, idx) => (
                              <div 
                                key={msg.id || idx}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[90%] p-3 rounded-lg overflow-hidden ${
                                  msg.role === 'user' 
                                    ? 'bg-orange-100 text-orange-900' 
                                    : msg.role === 'admin'
                                    ? 'bg-blue-100 text-blue-900'
                                    : 'bg-gray-100 text-gray-900'
                                }`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    {msg.role === 'user' ? (
                                      <User className="w-3 h-3" />
                                    ) : msg.role === 'admin' ? (
                                      <Reply className="w-3 h-3" />
                                    ) : (
                                      <Bot className="w-3 h-3" />
                                    )}
                                    <span className="text-xs font-medium">
                                      {msg.role === 'user' ? 'Müşteri' : msg.role === 'admin' ? 'Admin' : 'AI Asistan'}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {formatMessageClock(msg)}
                                    </span>
                                  </div>
                                  <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                                </div>
                              </div>
                              ))
                            )}
                        </div>
                        
                        {/* Admin Reply Section */}
                        <div className="p-4 border-t bg-gray-50">
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Admin olarak cevap yazın..."
                              value={replyMessage[session.id] || ''}
                              onChange={(e) => handleReplyChange(session.id, e.target.value)}
                              className="min-h-[60px] resize-none"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  sendAdminReply(session.id);
                                }
                              }}
                            />
                            <Button
                              onClick={() => sendAdminReply(session.id)}
                              disabled={!replyMessage[session.id]?.trim() || sendingReply[session.id]}
                              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                            >
                              {sendingReply[session.id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Enter tuşuyla gönder, Shift+Enter ile yeni satır
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatManagementTab;
